import mixpanel from "mixpanel-browser";
const mixpanelInitialized = process.env.MIXPANEL_ENABLE === "true" || false;

export const initMixpanel = (): void => {
  try {
    if (mixpanelInitialized) {
      const mixpanelKey = process.env.MIXPANEL_KEY;
      if (!mixpanelKey) {
        console.error("Mixpanel key is not defined.");
        return;
      }

      mixpanel.init(mixpanelKey, {
        debug: true,
        persistence: "localStorage",
      });
    }
  } catch (error) {
    console.error("Mixpanel initialization failed:", error);
  }
};

export const trackEvent = (
  eventName: string,
  eventData: Record<string, any>
) => {
  if (mixpanelInitialized) {
    try {
      mixpanel?.track(eventName, eventData);
    } catch (error) {
      console.error("Mixpanel initialization failed:", error);
    }
  }
};

export const trackPageView = () => {
  if (mixpanelInitialized) {
    try {
      const pageTitle = document.title;
      const pageURL = window.location.pathname;
      const referrerURL = document.referrer;

      let userType = localStorage.getItem("userType");
      if (!userType) {
        userType = "New User";
        localStorage.setItem("userType", userType);
      } else {
        userType = "Returning User";
      }

      const pageLoadTime = Math.round(performance.now() / 1000);

      const userDeviceType = /Mobi|Android/i.test(navigator.userAgent)
        ? "Mobile"
        : "Desktop";

      const userBrowserLanguage = navigator.language || "en-US";

      let pageInteractionCount = 0;
      if (typeof window !== "undefined") {
        document.addEventListener("click", () => {
          pageInteractionCount += 1;
        });
      }

      mixpanel?.track("Page View", {
        "Page URL": pageURL,
        "Referrer URL": referrerURL,
        "Page Title": pageTitle,
        "User Type": userType,
        "Page Load Time": pageLoadTime,
        "User Device Type": userDeviceType,
        "User Browser Language": userBrowserLanguage,
        "Page Interaction Count": pageInteractionCount,
      });
    } catch (error) {
      console.error("Mixpanel initialization failed:", error);
    }
  }
};

export const trackCTAClick = (
  buttonName: string,
  position: string,
  timeOnPage: number,
  clickCount: number,
  path: string
) => {
  if (mixpanelInitialized) {
    try {
      mixpanel?.track("CTA Clicked", {
        "CTA Button Name": buttonName,
        "CTA Position": position,
        "Time on Page Before CTA": timeOnPage,
        "CTA Click Count": clickCount,
        "CTA Path": path,
      });
    } catch (error) {
      console.error("Mixpanel initialization failed:", error);
    }
  }
};
export type ScrollData = {
  scrollPercentage: number;
  scrollCompletionTime?: number;
  scrollSpeed?: number;
  scrollInteractionSpeed?: { [section: string]: number };
  contentSectionsReached?: string[];
  lastInteractionBeforeScroll?: string;
};

export const trackScroll = (scrollData: ScrollData): void => {
  if (mixpanelInitialized) {
    try {
      mixpanel?.track("Scroll Tracking", {
        "Scroll Percentage": scrollData.scrollPercentage,
        "Scroll Completion Time (s)": scrollData.scrollCompletionTime,
        "Scroll Speed (px/s)": scrollData.scrollSpeed,
        "Scroll Interaction Speed (px/s per section)":
          scrollData.scrollInteractionSpeed,
        "Content Sections Reached": scrollData.contentSectionsReached,
        "Last Interaction Before Scroll":
          scrollData.lastInteractionBeforeScroll,
      });
    } catch (error) {
      console.error("Mixpanel initialization failed:", error);
    }
  }
};

export const trackPageExit = (exitData: {
  exitPage: string;
  exitIntent: boolean;
  interactionBeforeExit: string;
  exitTiming: string;
  exitSource: string;
  timeSpentInSession: number;
  exitPageType: string;
}): void => {
  if (mixpanelInitialized) {
    try {
      mixpanel.track("Page Exit", {
        "Exit Page": exitData.exitPage,
        "Exit Intent": exitData.exitIntent,
        "Interaction Before Exit": exitData.interactionBeforeExit,
        "Exit Timing": exitData.exitTiming,
        "Exit Source": exitData.exitSource,
        "Time Spent in Session": exitData.timeSpentInSession,
        "Exit Page Type": exitData.exitPageType,
      });
    } catch (error) {
      console.error("Mixpanel initialization failed:", error);
    }
  }
};

export const trackTimeSpendExit = (data: {
  pageDuration: number;
  averageInteractionTime: number;
  activeTime: number;
  idleTime: number;
  interactionCount: number;
  idleDetectionTriggered: boolean;
  engagementScore: number;
}): void => {
  if (mixpanelInitialized) {
    try {
      mixpanel.track("Time Spent on Page", {
        "Page Duration": data.pageDuration,
        "Average Interaction Time": data.averageInteractionTime,
        "Active Time": data.activeTime,
        "Idle Time": data.idleTime,
        "Interaction Count": data.interactionCount,
        "Idle Detection Triggered": data.idleDetectionTriggered,
        "Engagement Score": data.engagementScore,
      });
    } catch (error) {
      console.error("Mixpanel initialization failed:", error);
    }
  }
};

export const trackSectionStart = (data: {
  sessionStartTime: string;
  entryPage: string;
  sessionDeviceType: string;
  sessionBrowserVersion: string;
  operatingSystem: string;
}) => {
  if (mixpanelInitialized) {
    try {
      mixpanel.track("Session Start", {
        sessionStartTime: data.sessionStartTime,
        entryPage: data.entryPage,
        sessionDeviceType: data.sessionDeviceType,
        sessionBrowserVersion: data.sessionBrowserVersion,
        operatingSystem: data.operatingSystem,
      });
    } catch (error) {
      console.error("Mixpanel trackSessionStart failed:", error);
    }
  }
};

export const trackSectionEnd = (data: {
  sessionEndTime: string;
  sessionDuration: number;
  lastPageViewed: string;
  exitInteraction: string;
  lastInteraction: string;
  sessionEngagementScore: number;
  totalPagesViewed: number;
}) => {
  if (mixpanelInitialized) {
    try {
      mixpanel.track("Session End", {
        sessionEndTime: data.sessionEndTime,
        sessionDuration: data.sessionDuration,
        lastPageViewed: data.lastPageViewed,
        exitInteraction: data.exitInteraction,
        lastInteraction: data.lastInteraction,
        sessionEngagementScore: data.sessionEngagementScore,
        totalPagesViewed: data.totalPagesViewed,
      });
    } catch (error) {
      console.error("Mixpanel initialization failed:", error);
    }
  }
};

export const trackFormSubmission = (
  FormName: string,
  completionTime: number,
  fieldCompletionRate: number,
  fieldAbandonment: string[],
  errors: Record<string, any>,
  formData: Record<string, any>,
  path: string
) => {
  if (mixpanelInitialized) {
    try {
      mixpanel.track("Form Submission", {
        "Form Name": FormName,
        "Submission Time": completionTime.toString(),
        "Form Completion Status": true,
        "Form Completion Rate": fieldCompletionRate,
        "Field Abandonment": fieldAbandonment,
        "User Input Device": /Mobi|Android/i.test(navigator.userAgent)
          ? "Mobile"
          : "Desktop",
        "Form Errors Count": Object.keys(errors).length,
        "Submission Device Orientation":
          window.innerWidth > window.innerHeight ? "Landscape" : "Portrait",
        "Form Completion Path": path,
        "Form Details": formData,
      });
    } catch (error) {
      console.error("Mixpanel form submission tracking failed:", error);
    }
  }
};

export const trackFormSubmissionError = (
  errorMessage?: string,
  FormName?: string
) => {
  if (mixpanelInitialized) {
    try {
      mixpanel.track("Form Submission Error", {
        "Error Message": errorMessage,
        "Form Name": FormName,
        "Form Submission Failed": true,
      });
    } catch (error) {
      console.error("Mixpanel form error tracking failed:", error);
    }
  }
};

export const trackEventPreEnrolment = (
  eventName: PreEnrolmentEvent,
  properties?: Record<string, any>
): void => {
  try {
    if (mixpanelInitialized) {
      mixpanel.track(eventName, properties);
    }
  } catch (error) {
    console.error("Mixpanel trackEvent failed:", error);
  }
};

type PreEnrolmentEvent =
  | "study_enquiry_clicked"
  | "enrol_now_clicked"
  | "lead_form_opened"
  | "lead_form_submitted"
  | "lead_form_abandoned"
  | "otp_sent"
  | "otp_verified"
  | "otp_resend_clicked"
  | "reserve_seat_clicked"
  | "program_of_interest_changed"
  | "check_eligibility_clicked"
  | "nqf_level_selected"
  | "find_perfect_program_clicked"
  | "program_recommendation_clicked"
  | "learn_more_clicked"
  | "available_payment_plans_clicked"
  | "reserve_seat_final_clicked"
  | "retry_payment_clicked"
  | "payment_success_page_viewed"
  | "complete_application_clicked"
  | "go_to_homepage_clicked"
  | "otp_verify_clicked"
  | "change_phone_number_clicked";
